/**
 * `/features/:slug` — public per-product pages (#1719, round 7).
 *
 * Round 6 built these as thin 4-section pages; the founder rejected
 * that scale ("I wanted a full breakdown of the stuff on a separate
 * products page … subsections for details on the products"). Round 7
 * is the full Timble-scale product page: intro block (kicker + H1 +
 * blurb + hero media), a sticky anchor subnav, GROUPED subsections
 * clustering the product's real capabilities from `docs/USER_FLOW.md`
 * (the binding as-is behaviour doc), a "works with" strip cross-linking
 * sibling products, and the shared marketing chrome
 * (`../marketing/MarketingChrome.tsx`).
 *
 * `FEATURES` below is the single source of truth shared with the
 * landing: the landing's `02` showcase blocks render name + blurb +
 * `showcase` composite slot from this array, and the footer/nav link
 * from it too, so the surfaces can never drift apart.
 *
 * **Media placeholders.** Every `media` entry renders a `<MediaSlot/>`
 * — a token-native empty frame with a mono caption. They are the slots
 * a later agent fills with real screenshots / short videos; the fill
 * brief is `docs/agent-prompts/handover-feature-pages-media-2026-08-01.md`,
 * keyed by each slot's stable `id` (`data-media-slot` in the DOM). The
 * `composite` shape (round 7) is the Timble showcase composition — a
 * browser frame with an overlapping phone frame. Do NOT invent copy
 * inside the frames — the frame + caption IS the design until assets
 * land. Existing slot ids are stable; never rename casually.
 *
 * **Register** is the landing's: sage pin, default sans headings, mono
 * micro-labels, hairlines, one radius, no stock art, no emoji. Copy
 * rules apply in full (banlist; "weighted workload" / "hours worked",
 * never "score"; no AI-magic phrasing — the claim is our algorithms;
 * normal sentences, no crafted word-pile fragments). This file is
 * scanned by `tests/leadSurfaceForbiddenStrings.test.ts`.
 *
 * Every factual claim on these pages is grounded in a live surface —
 * `docs/USER_FLOW.md` is the arbiter. Do not add a claim without a
 * mechanism behind it. Never claim: cross-cycle on-call equity,
 * task-mode boards, usage dashboards, leave bidding, or integrations
 * (we have none).
 */
import { ArrowLeft, ArrowRight, Image, Play } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { MemberPhoneStory } from "./MemberPhoneStory";

import { Button } from "../../components/ui";
import { hasClerk } from "../../lib/clerk";
import { applyTheme, getTheme } from "../../lib/theme";
import { LandingMotion } from "../marketing/LandingMotion";
import { MarketingFooter, MarketingHeader } from "../marketing/MarketingChrome";
import { Reveal } from "../marketing/Reveal";
import { ScrollRail } from "../marketing/ScrollRail";
import { useHeaderOffset } from "../marketing/useHeaderOffset";
import "../marketing/roster-journey.css";
import "../marketing/landing-palette.css";
import "./feature-pages.css";
import { MediaSwitcher } from "./MediaSwitcher";
import { MEDIA_ASSETS } from "./mediaAssets";

// ─────────────────────────────────────────────────────────────────────
// Data — the five products, shared with the landing.
// ─────────────────────────────────────────────────────────────────────

export interface FeatureMediaSlot {
  /** Stable id the media-fill brief keys on. Never rename casually. */
  id: string;
  kind: "screenshot" | "video";
  /** Mono caption inside the frame, e.g. "Solve console". */
  label: string;
  /**
   * Frame shape: `wide` = desktop surface, `phone` = member app,
   * `composite` = Timble showcase composition — a browser frame with an
   * overlapping phone frame (one asset, one id).
   */
  shape: "wide" | "phone" | "composite";
}

interface FeatureSubsection {
  heading: string;
  body: string;
  media?: FeatureMediaSlot;
}

export interface FeatureGroup {
  /** Anchor id for the page subnav — stable, kebab-case. */
  anchor: string;
  /** Short subnav label, e.g. "The solve". */
  title: string;
  /** Optional one-sentence group lede. */
  intro?: string;
  subsections: ReadonlyArray<FeatureSubsection>;
}

export interface FeatureDef {
  slug: string;
  /** Product name — showcase title and page H1. */
  name: string;
  /** Mono kicker on the page, e.g. "For leads". */
  kicker: string;
  /** One sentence — footer rows and works-with strips. */
  oneLiner: string;
  /** 2–3 sentences — the landing showcase blurb and the page lede. */
  blurb: string;
  /** The page's lead media slot, under the H1. */
  hero: FeatureMediaSlot;
  /** The landing showcase's composite media slot. */
  showcase: FeatureMediaSlot;
  /** Which landing ledger diagram accents the showcase block. */
  art: "solve" | "deliver" | "absorb" | "swap" | "see";
  groups: ReadonlyArray<FeatureGroup>;
  /** Sibling slugs for the "works with" strip. */
  worksWith: ReadonlyArray<string>;
}

export const FEATURES: ReadonlyArray<FeatureDef> = [
  {
    slug: "solver",
    name: "Roster solving",
    kicker: "For leads",
    oneLiner: "Tarii builds the roster. You review and publish.",
    blurb:
      "You define the stations, people and rules. Tarii builds a roster for you to review. When it cannot fill a shift, it explains why.",
    art: "solve",
    hero: {
      id: "solver-console",
      kind: "video",
      label: "A solve, start to finish",
      shape: "wide",
    },
    showcase: {
      id: "solver-showcase",
      kind: "screenshot",
      label: "The solve console beside the roster",
      shape: "composite",
    },
    worksWith: ["requests", "swaps", "publishing"],
    groups: [
      {
        anchor: "setup",
        title: "Setting up",
        subsections: [
          {
            heading: "A guided start",
            body: "Set up your stations, working hours, role levels and leave types in 11 guided steps. Preview the setup before you apply it.",
            media: {
              id: "setup-guided",
              kind: "screenshot",
              label: "The guided setup",
              shape: "wide",
            },
          },
          {
            heading: "Stations with floors and ceilings",
            body: "Set when each station runs, how many people it needs and who can work there. Add its priority and any conditions, such as rest after on-call.",
            media: {
              id: "setup-stations",
              kind: "screenshot",
              label: "The stations ledger",
              shape: "wide",
            },
          },
          {
            heading: "Rules and fairness on one sheet",
            body: "Choose which rules must hold and how to balance the workload. Start with the standard weights or set your own.",
          },
          {
            heading: "Attributes and shift mix",
            body: "Define custom attributes that will impact how the solver rosters your members.",
            media: {
              id: "setup-attributes",
              kind: "screenshot",
              label: "The attribute catalogue",
              shape: "wide",
            },
          },
          {
            heading: "Standing duties",
            body: "Assign recurring duties to a member, such as every Wednesday or the first and third week. The solver builds around those assignments.",
          },
          {
            heading: "Every capability is a switch",
            body: "Turn each component on or off for your team. Each one tells you where it appears in Tarii.",
          },
        ],
      },
      {
        anchor: "solve",
        title: "The solve",
        subsections: [
          {
            heading: "One pass, every rule in it",
            body: "The solver considers coverage, eligibility, rest and workload limits together. Each station keeps its own conditions.",
            media: {
              id: "solver-grid",
              kind: "screenshot",
              label: "The solved grid",
              shape: "wide",
            },
          },
          {
            heading: "One console, one decision",
            body: "Choose the dates and stations to solve. Build the whole roster, run groups in order or choose a custom scope.",
          },
          {
            heading: "Watch it run",
            body: "Follow the run live, including elapsed time and solver progress. On a staged run, you can stop and keep the groups that finished.",
            media: {
              id: "solver-progress",
              kind: "screenshot",
              label: "Live solve progress",
              shape: "wide",
            },
          },
          {
            heading: "Start from what you have",
            body: "Refine the last solve, set a time limit or build several months of rosters in one run.",
          },
        ],
      },
      {
        anchor: "weighs",
        title: "What it weighs",
        subsections: [
          {
            heading: "Eight considerations, together",
            body: "The solver considers past workload, leave, contracted hours, attributes, station conditions, solve groups, fixed duties and preferences together.",
          },
          {
            heading: "Each one a setting",
            body: "Choose what the solver balances and how much weight each setting carries.",
          },
        ],
      },
      {
        anchor: "groups",
        title: "Solve groups",
        subsections: [
          {
            heading: "Groups first, then the rest",
            body: "Choose which groups of stations to solve first. Tarii fills them in the priority order you set.",
            media: {
              id: "solver-groups",
              kind: "screenshot",
              label: "Solve groups in order",
              shape: "wide",
            },
          },
          {
            heading: "Drafts until you release",
            body: "Review the results as drafts. Members see them only when you publish, one group at a time or all at once.",
          },
          {
            heading: "Rules hold across stages",
            body: "Later groups keep earlier assignments in place. Rest after an on-call duty still applies when the next group runs.",
          },
        ],
      },
      {
        anchor: "honesty",
        title: "Honesty",
        subsections: [
          {
            heading: "A flagged gap, never a green tick",
            body: "When no qualified person is free, Tarii flags the open shift and explains why it cannot fill it.",
            media: {
              id: "solver-gap",
              kind: "screenshot",
              label: "A flagged gap",
              shape: "wide",
            },
          },
          {
            heading: "Partial rosters explain themselves",
            body: "If Tarii can cover only some dates, the report names the blocked days and the leave affecting them. The report stays available after you reload.",
          },
          {
            heading: "An independent self-check",
            body: "A separate check tests every finished solve against your hard rules. You see the result beside the roster.",
          },
        ],
      },
      {
        anchor: "review",
        title: "Review & repair",
        subsections: [
          {
            heading: "Three ways to read the roster",
            body: "View the roster by member, by station or as a daily list. Each view shows the same coverage status.",
            media: {
              id: "solver-views",
              kind: "screenshot",
              label: "Grid, stations and list views",
              shape: "wide",
            },
          },
          {
            heading: "The numbers beside the grid",
            body: "See hours and weighted workload by level, changes from recent rosters and any coverage gaps. The figures update as you edit.",
          },
          {
            heading: "Corrections that show their work",
            body: "Preview changes to fill gaps and share the load. Tarii shows the roster before and after, and you choose whether to apply them.",
            media: {
              id: "solver-corrections",
              kind: "screenshot",
              label: "A corrections draft",
              shape: "wide",
            },
          },
          {
            heading: "Versions and variants",
            body: "Tarii saves a version after each solve, swap and restore. Compare candidate rosters side by side and restore the one you choose as a draft.",
            media: {
              id: "solver-versions",
              kind: "screenshot",
              label: "Versions and variants",
              shape: "wide",
            },
          },
        ],
      },
    ],
  },
  {
    slug: "member-app",
    name: "Member app",
    kicker: "For members",
    oneLiner: "Members see their shifts and send requests from their phone.",
    blurb:
      "Each member sees their own roster on their phone. They can send leave requests, report sick and ask for a swap in the same app.",
    art: "deliver",
    hero: {
      id: "member-walkthrough",
      kind: "video",
      label: "The member app in 30 seconds",
      shape: "phone",
    },
    showcase: {
      id: "member-showcase",
      kind: "screenshot",
      label: "The member calendar, desktop and phone",
      shape: "composite",
    },
    worksWith: ["requests", "swaps", "publishing"],
    groups: [
      {
        anchor: "calendar",
        title: "Calendar",
        subsections: [
          {
            heading: "Their roster, the moment it publishes",
            body: "Each member sees their published shifts by month, week or day, with estimated times and who is working with them. Past months stay available.",
            media: {
              id: "member-calendar",
              kind: "screenshot",
              label: "Calendar tab",
              shape: "phone",
            },
          },
          {
            heading: "Their own plans on the same grid",
            body: "Members add their own plans beside their shifts and adjust the times. These personal plans stay private.",
            media: {
              id: "member-timeblocks",
              kind: "screenshot",
              label: "A personal block beside a shift",
              shape: "phone",
            },
          },
          {
            heading: "A feed their phone subscribes to",
            body: "Members can add a personal feed of their published shifts to the calendar app they already use.",
          },
          {
            heading: "Honest about what's still being planned",
            body: "Dates you have not published stay marked as planned. Members can tell which shifts are confirmed.",
          },
        ],
      },
      {
        anchor: "inbox",
        title: "Inbox",
        subsections: [
          {
            heading: "An inbox that only asks when needed",
            body: "Members receive roster news, swap requests and reminders in one inbox. Cards show what needs action, and the badge counts those items.",
            media: {
              id: "member-inbox",
              kind: "screenshot",
              label: "Inbox tab",
              shape: "phone",
            },
          },
          {
            heading: "Every card can say why it exists",
            body: "Each event has one card that updates as things change. When the request is resolved, the card clears from the inbox.",
          },
          {
            heading: "An archive that explains itself",
            body: "The archive explains why a card cleared, such as a teammate accepting, a newer update arriving or the date passing.",
          },
          {
            heading: "Notifications on their terms",
            body: "Members choose which types of updates they receive by email. Roster updates always remain available in the app.",
          },
        ],
      },
      {
        anchor: "tasks",
        title: "Tasks & requests",
        subsections: [
          {
            heading: "Requests and tasks in one place",
            body: "Members see leave, sick reports, swaps and assigned tasks in one place, with the status of each item.",
            media: {
              id: "member-tasks",
              kind: "screenshot",
              label: "Tasks tab",
              shape: "phone",
            },
          },
          {
            heading: "Assigned work they can plan",
            body: "Members place assigned tasks in their own week and split the work across days when needed. They decide how to plan the time.",
            media: {
              id: "member-task-chip",
              kind: "screenshot",
              label: "Planning a task on the week",
              shape: "phone",
            },
          },
          {
            heading: "A note to the lead",
            body: "Before you build the roster, members can send shift preferences and a private note to their team lead.",
          },
          {
            heading: "Feedback on any shift",
            body: "Members can give feedback on a shift and write to their team lead. The lead sees feedback in aggregate and named notes addressed to them.",
          },
        ],
      },
      {
        anchor: "privacy",
        title: "Privacy",
        subsections: [
          {
            heading: "Private by construction",
            body: "Only the member sees their personal calendar plans. Team leads have no view of them.",
          },
          {
            heading: "Timing is theirs to share",
            body: "The team lead sees whether an assigned task is planned and done. Members choose whether to share its timing and can stop sharing at any time.",
          },
          {
            heading: "No individual scores or rankings",
            body: "See how hours and shifts are shared across the roster, without individual scores or rankings.",
          },
          {
            heading: "Their data, exportable",
            body: "Members can export their own data and read their activity record on any plan.",
          },
        ],
      },
    ],
  },
  {
    slug: "requests",
    name: "Leave & requests",
    kicker: "For the whole team",
    oneLiner: "Members send requests. You review them in one queue.",
    blurb:
      "Members send leave requests, sick reports and preferences from their phone. You review them in one queue, and Tarii uses them when building the roster.",
    art: "absorb",
    hero: {
      id: "requests-flow",
      kind: "screenshot",
      label: "A request, start to end",
      shape: "phone",
    },
    showcase: {
      id: "requests-showcase",
      kind: "screenshot",
      label: "The review queue and the member's request",
      shape: "composite",
    },
    worksWith: ["solver", "member-app", "publishing"],
    groups: [
      {
        anchor: "lifecycle",
        title: "One request store",
        subsections: [
          {
            heading: "One request store, one lifecycle",
            body: "Track each request from submission to planning and confirmation. Tarii updates its status as you solve and publish the roster.",
          },
          {
            heading: "Honest from the first tap",
            body: "Members choose the dates and leave type, then review the impact. The form explains when the lead sees the request and when publication confirms it.",
            media: {
              id: "requests-flow-steps",
              kind: "screenshot",
              label: "The three-step request flow",
              shape: "phone",
            },
          },
          {
            heading: "Partial answers, stated plainly",
            body: "Members see which dates are confirmed and which are still planned. If you decline a request, they see the reason and can reply once.",
          },
        ],
      },
      {
        anchor: "review",
        title: "The lead's review",
        subsections: [
          {
            heading: "A review queue for the lead",
            body: "Review requests in one queue, ordered by urgency and type. Accept them into the plan, ask to move them or decline with a reason. Unreviewed requests still enter the solve.",
            media: {
              id: "requests-queue",
              kind: "screenshot",
              label: "The lead's review queue",
              shape: "wide",
            },
          },
          {
            heading: "It never blocks a solve",
            body: "Requests enter the solver when members submit them. You can review them without holding up the next solve.",
          },
          {
            heading: "Your own categories",
            body: "Define your leave types and choose how each one counts in workload balance and the review order.",
            media: {
              id: "requests-categories",
              kind: "screenshot",
              label: "The leave-type catalogue",
              shape: "wide",
            },
          },
        ],
      },
      {
        anchor: "windows",
        title: "Request windows and reminders",
        subsections: [
          {
            heading: "Request windows and reminders",
            body: "Set a request deadline and see who has submitted. Send a reminder to the remaining members with one press.",
            media: {
              id: "requests-submissions",
              kind: "screenshot",
              label: "Submissions panel",
              shape: "wide",
            },
          },
          {
            heading: "Reminders that update, never stack",
            body: "Each request window has one reminder card. It updates as the deadline approaches.",
          },
        ],
      },
      {
        anchor: "solve",
        title: "Into the solve",
        subsections: [
          {
            heading: "Approved leave binds hard",
            body: "The solver keeps confirmed time off in place. It considers preferences where coverage allows.",
          },
          {
            heading: "Leave weighs into the balance",
            body: "Choose how each leave type counts towards workload balance. Time away does not become work a member owes back.",
          },
          {
            heading: "Urgent asks find the cover flow",
            body: "When someone reports sick against a published shift, the team lead can open the cover options directly.",
          },
        ],
      },
    ],
  },
  {
    slug: "swaps",
    name: "Swaps & cover",
    kicker: "For the whole team",
    oneLiner: "Members swap shifts and find cover within your team's rules.",
    blurb:
      "Members request swaps from their phone. Team leads see who can cover an open shift, and Tarii checks the rules before assignments change.",
    art: "swap",
    hero: {
      id: "swaps-flow",
      kind: "video",
      label: "A swap, end to end",
      shape: "phone",
    },
    showcase: {
      id: "swaps-showcase",
      kind: "screenshot",
      label: "The swaps board and a member's ask",
      shape: "composite",
    },
    worksWith: ["member-app", "solver", "publishing"],
    groups: [
      {
        anchor: "swaps",
        title: "Member-raised swaps",
        subsections: [
          {
            heading: "Raised by members, checked by the rules",
            body: "Members offer swaps to eligible teammates. Tarii shows how many can accept and checks the rules again before changing the roster.",
          },
          {
            heading: "Refusals arrive up front",
            body: "If a team rule prevents a swap, the member sees the shift and the reason before sending a request.",
            media: {
              id: "swaps-raise",
              kind: "screenshot",
              label: "Raising a swap",
              shape: "phone",
            },
          },
          {
            heading: "Offer days in either direction",
            body: "Members can offer an earlier or later day in exchange. The form marks days with no available teammate before they submit.",
          },
          {
            heading: "Held when the mix would thin",
            body: "If a swap would leave the shift short of its required mix, it waits for the team lead to confirm. The roster stays unchanged while it waits.",
          },
        ],
      },
      {
        anchor: "policy",
        title: "The lead's policy",
        subsections: [
          {
            heading: "Swap rules the team can read",
            body: "Set how much notice swaps need, which stations allow them and whether on-call duties can be swapped. Members see these rules before asking.",
          },
        ],
      },
      {
        anchor: "board",
        title: "The board",
        subsections: [
          {
            heading: "The lead stays in the loop",
            body: "The team lead sees open swaps and their history in one board. Assign cover, decline with a reason or let eligible members accept.",
            media: {
              id: "swaps-board",
              kind: "screenshot",
              label: "The swaps board",
              shape: "wide",
            },
          },
        ],
      },
      {
        anchor: "cover",
        title: "Sick cover",
        subsections: [
          {
            heading: "Sick cover without the group chat",
            body: "Send a cover request to eligible members without naming the person who is off. When someone accepts, Tarii records the cover.",
          },
          {
            heading: "Cover options, and what each costs",
            body: "See who is free, who could return from rest and whose leave could be cancelled. Each option explains what it would change.",
          },
          {
            heading: "Help that gets counted",
            body: "Tarii records who covered for whom. Members can see how often they helped and received help.",
          },
        ],
      },
      {
        anchor: "grid",
        title: "On the grid",
        subsections: [
          {
            heading: "Drag to swap on the grid",
            body: "Drag one assignment onto another to exchange them. If a rule prevents the swap, Tarii explains why.",
            media: {
              id: "swaps-drag",
              kind: "video",
              label: "Drag-to-swap on the roster",
              shape: "wide",
            },
          },
          {
            heading: "Every move on the record",
            body: "Tarii saves the roster before a swap and records both assignments in the change history.",
          },
        ],
      },
    ],
  },
  {
    slug: "publishing",
    name: "Publishing & records",
    kicker: "For leads",
    oneLiner: "Publish the roster and keep the team informed when it changes.",
    blurb:
      "Publish once or in stages. Each member sees their own roster, and affected members receive updates when it changes. Review hours and workload across the team.",
    art: "see",
    hero: {
      id: "publishing-release",
      kind: "screenshot",
      label: "The release curtain",
      shape: "wide",
    },
    showcase: {
      id: "publishing-showcase",
      kind: "screenshot",
      label: "The release checklist and a member's notice",
      shape: "composite",
    },
    worksWith: ["solver", "member-app", "requests"],
    groups: [
      {
        anchor: "release",
        title: "Release",
        subsections: [
          {
            heading: "All at once, or in stages",
            body: "Publish the whole roster or choose groups, stations and dates. Members see only the shifts you have released.",
          },
          {
            heading: "A checklist that says what happens",
            body: "Before you publish, see how many assignments will go out, which requests will be confirmed and how the workload is shared.",
          },
          {
            heading: "Take it back, on the record",
            body: "Take back a published part of the roster when needed. It returns to draft, disappears from members' views and stays in the release history.",
            media: {
              id: "publishing-history",
              kind: "screenshot",
              label: "The release history",
              shape: "wide",
            },
          },
        ],
      },
      {
        anchor: "notify",
        title: "Tell the team",
        subsections: [
          {
            heading: "Everyone hears it once, personally",
            body: "Each member receives their own shift totals when you publish, including weekends and on-call. The notice makes clear if only part of the roster is out.",
            media: {
              id: "publishing-notice",
              kind: "screenshot",
              label: "“Your roster is out”",
              shape: "phone",
            },
          },
          {
            heading: "Changes arrive as one story",
            body: "After edits or swaps, affected members receive a combined update. The notice and change history use the same record.",
          },
        ],
      },
      {
        anchor: "records",
        title: "Records",
        subsections: [
          {
            heading: "A record the whole team can stand behind",
            body: "Review weighted workload and hours worked side by side, grouped by level. Compare them with recent rosters.",
            media: {
              id: "publishing-workload",
              kind: "screenshot",
              label: "Workload, side by side",
              shape: "wide",
            },
          },
          {
            heading: "Fairness that carries forward",
            body: "Tarii carries workload balance into the next roster. It counts published work, so drafts do not affect the balance.",
          },
          {
            heading: "On-call history in view",
            body: "See recent on-call duties while you review the next roster.",
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────
// The drawn product marks — small line-art SVGs in tokens (round 7,
// the Timble icon slot). Each depicts the product's real behaviour in
// the roster-cell vocabulary; all aria-hidden, no clip-art, no emoji.
// ─────────────────────────────────────────────────────────────────────

const MARK_STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function MarkSolve() {
  // A week grid with one cell settling in — the solve.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      <g {...MARK_STROKE}>
        <rect x="3" y="5" width="26" height="22" rx="2.5" />
        <path d="M3 12h26M11.7 12v15M20.3 12v15" />
      </g>
      <rect x="14" y="15" width="4" height="4" rx="1" className="fill-current" />
    </svg>
  );
}

function MarkMemberApp() {
  // A phone with a schedule row inside.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      <g {...MARK_STROKE}>
        <rect x="9" y="3" width="14" height="26" rx="3" />
        <path d="M13 24.5h6" />
      </g>
      <rect x="12.5" y="9" width="7" height="3" rx="1" className="fill-current opacity-60" />
      <rect x="12.5" y="14" width="7" height="3" rx="1" className="fill-current opacity-30" />
    </svg>
  );
}

function MarkRequests() {
  // A request landing in the team's tray.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      <g {...MARK_STROKE}>
        <path d="M16 3v10m0 0-4-4m4 4 4-4" />
        <path d="M4 17v7a3 3 0 0 0 3 3h18a3 3 0 0 0 3-3v-7" />
        <path d="M4 20h7l2 3h6l2-3h7" />
      </g>
    </svg>
  );
}

function MarkSwaps() {
  // Two assignments exchanging places.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      <g {...MARK_STROKE}>
        <path d="M11 8h13m0 0-4-4m4 4-4 4" />
        <path d="M21 24H8m0 0 4 4m-4-4 4-4" />
      </g>
    </svg>
  );
}

function MarkPublishing() {
  // The release curtain — rows revealed left of the line, waiting right.
  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden="true">
      <g {...MARK_STROKE}>
        <path d="M19 5v22" strokeDasharray="2.5 3" />
      </g>
      <rect x="4" y="7" width="11" height="4" rx="1" className="fill-current opacity-60" />
      <rect x="4" y="14" width="11" height="4" rx="1" className="fill-current opacity-60" />
      <rect x="4" y="21" width="11" height="4" rx="1" className="fill-current opacity-60" />
      <g {...MARK_STROKE} className="opacity-50">
        <rect x="23" y="7" width="5" height="4" rx="1" />
        <rect x="23" y="14" width="5" height="4" rx="1" />
        <rect x="23" y="21" width="5" height="4" rx="1" />
      </g>
    </svg>
  );
}

export const PRODUCT_MARKS: Record<string, () => JSX.Element> = {
  solver: MarkSolve,
  "member-app": MarkMemberApp,
  requests: MarkRequests,
  swaps: MarkSwaps,
  publishing: MarkPublishing,
};

// ─────────────────────────────────────────────────────────────────────
// The media placeholder frame.
// ─────────────────────────────────────────────────────────────────────

const SLOT_GRID_STYLE = {
  backgroundImage:
    "linear-gradient(rgb(var(--color-border) / 0.5) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--color-border) / 0.5) 1px, transparent 1px)",
  backgroundSize: "28px 28px",
} as const;

/**
 * A media slot: a real screenshot / short video when its asset exists
 * (`MEDIA_ASSETS`, assets in `web/public/features/` — fill brief:
 * `docs/agent-prompts/handover-feature-pages-media-2026-08-01.md`),
 * otherwise an empty, deliberate frame — hairline border, faint cell
 * grid, a mono caption — so the page reads finished, never broken.
 * Filled or not, the asset sits INSIDE the same hairline frame; the
 * frame is the design. `data-media-slot` carries the stable id.
 *
 * `composite` (round 7) is the Timble showcase shape: a browser frame
 * with a chrome bar, and a phone frame overlapping its lower corner.
 * One slot id covers the whole composition — its asset entry carries a
 * browser capture and a phone capture.
 */
export function MediaSlot({ slot }: { slot: FeatureMediaSlot }) {
  const asset = MEDIA_ASSETS[slot.id];
  const Icon = slot.kind === "video" ? Play : Image;
  const caption = (
    <>
      <Icon aria-hidden="true" className="h-4 w-4 text-fg-faint" strokeWidth={1.5} />
      <span className="px-4 text-center font-mono text-[9.5px] text-fg-subtle uppercase tracking-[0.18em]">
        {slot.kind === "video" ? "Video" : "Screenshot"} · {slot.label}
      </span>
    </>
  );

  if (slot.shape === "composite") {
    const composite = asset?.kind === "composite" ? asset : undefined;
    return (
      <figure data-media-slot={slot.id} className="relative w-full pb-10 pl-4 sm:pb-12 sm:pl-8">
        {/* The browser frame. */}
        <div className="relative overflow-hidden rounded-[4px] border border-border-strong bg-bg-subtle/60">
          <div className="flex items-center gap-1.5 border-border border-b px-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-border-strong" />
            <span className="h-1.5 w-1.5 rounded-full bg-border-strong" />
            <span className="h-1.5 w-1.5 rounded-full bg-border-strong" />
          </div>
          <div className="relative aspect-[16/9]">
            {composite ? (
              <img
                src={composite.browser}
                alt={slot.label}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-contain object-center"
              />
            ) : (
              <>
                <div aria-hidden="true" className="absolute inset-0" style={SLOT_GRID_STYLE} />
                {/* Caption shifted off the phone frame's overlap zone. */}
                <figcaption className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 pl-[22%] pr-[3%]">
                  {caption}
                </figcaption>
              </>
            )}
          </div>
        </div>
        {/* The overlapping phone frame. */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-[24%] min-w-[4.5rem] max-w-[7rem] overflow-hidden rounded-[8px] border border-border-strong bg-bg"
        >
          <div className="relative aspect-[9/17]">
            {composite ? (
              <img
                src={composite.phone}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-contain object-center"
              />
            ) : (
              <>
                <div className="absolute inset-0" style={SLOT_GRID_STYLE} />
                <div className="absolute inset-x-0 top-1.5 flex justify-center">
                  <span className="h-1 w-6 rounded-full bg-border-strong" />
                </div>
              </>
            )}
          </div>
        </div>
      </figure>
    );
  }

  return (
    <figure
      data-media-slot={slot.id}
      className={`relative overflow-hidden rounded-[3px] border border-border-strong bg-bg-subtle/60 ${
        slot.shape === "phone" ? "mx-auto aspect-[9/16] w-48 sm:w-56" : "aspect-[16/10] w-full"
      }`}
    >
      {asset?.kind === "image" ? (
        <img
          src={asset.src}
          alt={slot.label}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-contain object-center"
        />
      ) : asset?.kind === "video" ? (
        // Per the fill brief: short, muted, looping, inline — the
        // poster keeps the frame calm until playback starts.
        <video
          src={asset.src}
          poster={asset.poster}
          muted
          loop
          autoPlay
          playsInline
          aria-label={slot.label}
          className="absolute inset-0 h-full w-full object-contain object-center"
        />
      ) : (
        <>
          <div aria-hidden="true" className="absolute inset-0" style={SLOT_GRID_STYLE} />
          <figcaption className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
            {caption}
          </figcaption>
        </>
      )}
    </figure>
  );
}

// ─────────────────────────────────────────────────────────────────────

const H_PAGE =
  "font-semibold text-[2.25rem] text-fg leading-[1.08] tracking-tight sm:text-[2.75rem]";

/** Shared close — the same CTA shape the landing's 04 uses. */
export function MarketingClose({ heading = "Start with your next roster." }: { heading?: string }) {
  return (
    <div className="flex flex-col gap-6 border-border border-t pt-12 sm:flex-row sm:items-end sm:justify-between">
      <h2 className="max-w-[18ch] font-semibold text-[1.75rem] text-fg leading-[1.15] tracking-tight">
        {heading}
      </h2>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        {hasClerk ? (
          <Button asChild size="lg" className="group h-11 px-6 text-[0.9375rem]">
            <Link to="/start">
              Sign up
              <ArrowRight
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
              />
            </Link>
          </Button>
        ) : (
          <Button asChild size="lg" className="h-11 px-6 text-[0.9375rem]">
            <Link to="/sign-in">
              Sign in
              <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
        <Button
          asChild
          size="lg"
          variant="ghost"
          className="h-11 px-0 font-normal text-[0.9375rem] underline-offset-[6px] hover:bg-transparent hover:text-fg hover:underline"
        >
          <Link to="/demo">Try the demo</Link>
        </Button>
      </div>
    </div>
  );
}

function SetupStory({
  group,
  solverSetup = false,
}: { group: FeatureGroup; solverSetup?: boolean }) {
  const storyId = useId();
  const [active, setActive] = useState(0);
  const section = group.subsections[active];
  const labels = solverSetup
    ? [
        "Guided start",
        "Stations",
        "Rules & fairness",
        "Attributes",
        "Standing duties",
        "Components",
      ]
    : group.subsections.map((part) => part.heading);
  if (!section) return null;
  return (
    <div className="feature-setup-story">
      <div
        className="feature-setup-timeline"
        style={{
          gridTemplateColumns: `repeat(${group.subsections.length}, minmax(0, 1fr))`,
        }}
        role="tablist"
        aria-label={`${group.title} stages`}
      >
        {group.subsections.map((part, index) => (
          <button
            key={part.heading}
            type="button"
            role="tab"
            id={`${storyId}-stage-${index}`}
            aria-controls={`${storyId}-panel`}
            aria-selected={active === index}
            tabIndex={active === index ? 0 : -1}
            data-complete={index < active}
            onMouseEnter={() => setActive(index)}
            onClick={() => setActive(index)}
            onKeyDown={(event) => {
              let next = index;
              if (event.key === "ArrowRight") next = (index + 1) % group.subsections.length;
              else if (event.key === "ArrowLeft")
                next = (index + group.subsections.length - 1) % group.subsections.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = group.subsections.length - 1;
              else return;
              event.preventDefault();
              setActive(next);
              document.getElementById(`${storyId}-stage-${next}`)?.focus();
            }}
          >
            <span className="feature-setup-tile" aria-hidden="true">
              {index + 1}
            </span>
            <span>{labels[index]}</span>
          </button>
        ))}
      </div>
      <div id={`${storyId}-panel`} role="tabpanel" aria-labelledby={`${storyId}-stage-${active}`}>
        <Reveal key={section.heading} className="feature-setup-stage">
          <div className="feature-setup-copy">
            <h3 className="font-medium text-[1.25rem] text-fg">{section.heading}</h3>
            <p className="mt-3 max-w-[68ch] whitespace-pre-line text-[1rem] text-fg-muted leading-[1.7]">
              {section.body}
            </p>
          </div>
          {section.media && (
            <div className="feature-setup-media">
              <MediaSlot slot={section.media} />
            </div>
          )}
        </Reveal>
      </div>
      <div className="feature-setup-actions">
        <button type="button" disabled={active === 0} onClick={() => setActive(active - 1)}>
          Previous
        </button>
        <span>
          {active + 1} of {group.subsections.length}
        </span>
        <button
          type="button"
          disabled={active === group.subsections.length - 1}
          onClick={() => setActive(active + 1)}
        >
          Continue <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** The public per-product page. Unknown slug → back to the landing. */
export function FeaturePage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const feature = FEATURES.find((f) => f.slug === slug);
  const [active, setActive] = useState("");
  useEffect(() => {
    setActive(feature?.groups[0]?.anchor ?? "");
    if (!feature || typeof IntersectionObserver === "undefined") return;
    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id);
          else inBand.delete(entry.target.id);
        }
        for (let i = feature.groups.length - 1; i >= 0; i--) {
          const group = feature.groups[i];
          if (group && inBand.has(group.anchor)) {
            setActive(group.anchor);
            return;
          }
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const group of feature.groups) {
      const element = document.getElementById(group.anchor);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [feature]);

  // Same theme pin as the landing (founder direction 2026-08-01): the
  // public surfaces render sage for everyone; the visitor's stored
  // preference is untouched and restored on unmount.
  useLayoutEffect(() => {
    applyTheme("sage-clinic");
    return () => applyTheme(getTheme());
  }, []);

  useHeaderOffset();
  // Cross-page anchor links (`/features/requests#review`) land on their
  // group. Runs per slug/hash; a missing anchor is simply ignored.
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    if (slug) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, slug]);

  if (!feature) return <Navigate to="/" replace />;

  const siblings = feature.worksWith
    .map((s) => FEATURES.find((f) => f.slug === s))
    .filter((f): f is FeatureDef => f !== undefined);

  return (
    <LandingMotion>
      <div data-la-root="" className="feature-public min-h-screen bg-bg">
        <div className="la-pinned-header">
          <div className="la-pinned-header-inner">
            <MarketingHeader />
          </div>
        </div>
        {feature.slug !== "member-app" && (
          <ScrollRail
            sections={feature.groups.map((group) => ({
              id: group.anchor,
              no: "",
              name: group.title,
              tint: null,
            }))}
            active={active}
          />
        )}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pt-24 pb-24 sm:px-10 lg:pl-56 lg:pb-0">
          <main className="py-12 sm:py-16">
            <div className={feature.slug === "member-app" ? "member-page-hero" : undefined}>
              <div className={feature.slug === "member-app" ? "member-page-intro" : undefined}>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center gap-1.5 font-medium text-[0.875rem] text-fg-subtle hover:text-fg"
                >
                  <ArrowLeft aria-hidden="true" className="h-3 w-3" />
                  How it works
                </Link>
                <p className="mt-8 font-medium text-[0.9375rem] text-accent-fg">{feature.kicker}</p>
                <h1 className={`mt-5 max-w-[22ch] ${H_PAGE}`}>{feature.name}</h1>
                {feature.slug === "solver" ? (
                  <div className="mt-5 max-w-[58ch] text-[1.0625rem] text-fg-muted leading-[1.65]">
                    <p>
                      Set your <strong className="font-bold">stations</strong>,{" "}
                      <strong className="font-bold">rules</strong>, and{" "}
                      <strong className="font-bold">what fair means to you</strong> once. Our
                      algorithm weighs all of it and returns the best roster your rules allow.
                    </p>
                    <p className="mt-4">Tarii tells you every trade-off and every issue.</p>
                  </div>
                ) : (
                  <p className="mt-5 max-w-[58ch] text-[1.0625rem] text-fg-muted leading-[1.65]">
                    {feature.blurb}
                  </p>
                )}
              </div>
              {feature.slug === "member-app" ? (
                <MemberPhoneStory />
              ) : (
                <Reveal className="mt-10">
                  <MediaSlot slot={feature.hero} />
                </Reveal>
              )}
            </div>
            {(feature.slug === "member-app" ? [] : feature.groups).map((group, i) => (
              <section
                key={group.anchor}
                id={group.anchor}
                aria-labelledby={`${feature.slug}-${group.anchor}`}
                className="feature-disclosure scroll-mt-24 border-border border-b py-6 last:border-b-0"
              >
                <details
                  key={`${feature.slug}-${group.anchor}`}
                  open={hash === `#${group.anchor}` ? true : undefined}
                >
                  <summary className="feature-group-summary">
                    <div className="flex items-baseline gap-4">
                      <span className="font-medium text-[0.875rem] text-accent-fg">0{i + 1}</span>
                      <h2
                        id={`${feature.slug}-${group.anchor}`}
                        className="font-semibold text-[1.625rem] text-fg leading-[1.15] tracking-tight sm:text-[1.875rem]"
                      >
                        {group.title}
                      </h2>
                    </div>
                    <span className="feature-disclosure-icon" aria-hidden="true">
                      +
                    </span>
                  </summary>
                  {group.intro ? (
                    <p className="mt-4 max-w-[58ch] text-[0.9375rem] text-fg-muted leading-[1.65]">
                      {group.intro}
                    </p>
                  ) : null}
                  {(feature.slug === "solver" && group.anchor === "setup") ||
                  (["requests", "member-app", "swaps"].includes(feature.slug) &&
                    group.subsections.length > 1) ? (
                    <SetupStory
                      key={`${feature.slug}-${group.anchor}`}
                      group={group}
                      solverSetup={feature.slug === "solver"}
                    />
                  ) : group.subsections.some((section) => section.media) ? (
                    <div className="feature-solve-layout mt-4">
                      <div className="feature-solve-copy">
                        {group.subsections.map((section) => (
                          <div key={section.heading} className="feature-subsection">
                            <h3 className="font-medium text-[1.125rem] text-fg tracking-[-0.005em]">
                              {section.heading}
                            </h3>
                            <p className="mt-2.5 text-[0.9375rem] text-fg-muted leading-[1.65]">
                              {section.body}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="feature-solve-media">
                        <MediaSwitcher
                          key={`${feature.slug}-${group.anchor}`}
                          items={group.subsections.flatMap((section) =>
                            section.media
                              ? [
                                  {
                                    id: section.media.id,
                                    label: section.heading,
                                    content: <MediaSlot slot={section.media} />,
                                  },
                                ]
                              : [],
                          )}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="feature-subsection-grid mt-4">
                      {group.subsections.map((section) => (
                        <div key={section.heading} className="feature-subsection">
                          <h3 className="font-medium text-[1.125rem] text-fg tracking-[-0.005em]">
                            {section.heading}
                          </h3>
                          <p className="mt-2.5 max-w-[58ch] text-[0.9375rem] text-fg-muted leading-[1.65]">
                            {section.body}
                          </p>
                          {section.media ? (
                            <Reveal className="mt-6">
                              <MediaSlot slot={section.media} />
                            </Reveal>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </details>
              </section>
            ))}

            {/* Works with — the sibling products this one hands off to. */}
            <section aria-label="Works with" className="py-12 sm:py-14">
              <span className="font-medium text-[0.9375rem] text-accent-fg">Works with</span>
              <div className="mt-6 grid gap-px overflow-hidden rounded-[12px] border border-border bg-border sm:grid-cols-3">
                {siblings.map((sibling) => {
                  const Mark = PRODUCT_MARKS[sibling.slug];
                  return (
                    <Link
                      key={sibling.slug}
                      to={`/features/${sibling.slug}`}
                      className="feature-related group flex flex-col gap-3 bg-bg p-6 transition-colors hover:bg-bg-subtle motion-reduce:transition-none"
                    >
                      <span className="text-fg-subtle">{Mark ? <Mark /> : null}</span>
                      <span className="inline-flex items-center gap-2 font-medium text-[0.9375rem] text-fg group-hover:text-accent-fg">
                        {sibling.name}
                        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5 text-fg-faint" />
                      </span>
                      <span className="text-[0.8125rem] text-fg-subtle leading-[1.6]">
                        {sibling.oneLiner}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>

            <MarketingClose />
          </main>

          <MarketingFooter />
        </div>
      </div>
    </LandingMotion>
  );
}
